import { createStandaloneToast } from "@chakra-ui/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "react-router-dom";
import { ployTheme } from "ui/theme";
import router from "./pages/Root";
import Appsignal from "@appsignal/javascript";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { ReactNode } from "react";
import IpRestrictedScreen from "components/IpRestrictedScreen";
import { useIpRestrictionStore } from "modules/members/api/ipRestriction";

export const appsignal = new Appsignal({
  key: "43a7b95b-76e8-45b6-ac05-b0c9a59c35c5",
  ignoreErrors: import.meta.env.PROD ? undefined : [/.*/],
});

export const { ToastContainer, toast: customToast } = createStandaloneToast({
  theme: ployTheme,
});

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      suspense: true,
      useErrorBoundary: true,
      retry: (failureCount, error: any) => {
        // Don't retry on 401 errors (unauthorized)
        if (error.status === 401) return false;
        return failureCount < 1;
      },
    },
  },
});

// Swaps the entire router out for the IP-block screen the moment any request
// comes back IP-restricted (PLO-5010). Unmounting the router is the point:
// it tears down every mounted query so nothing keeps retrying behind a screen
// that says the network is blocked, and no half-loaded shell shows through.
const IpRestrictionGate = ({ children }: { children: ReactNode }) => {
  const isIpRestricted = useIpRestrictionStore((s) => s.isIpRestricted);
  return isIpRestricted ? <IpRestrictedScreen /> : <>{children}</>;
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <IpRestrictionGate>
        <RouterProvider router={router} />
      </IpRestrictionGate>
      <ToastContainer />
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}

export default App;
